import React, {
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDebounce } from 'react-use';

import {
    Box,
    Button,
    CloseButton,
    Drawer,
    Flex,
    HStack,
    Popover,
    Portal,
    Skeleton,
    Text,
    useDisclosure,
    useSlotRecipe,
} from '@chakra-ui/react';

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { v4 as uuid } from 'uuid';

import { IconsLinear } from '@nocowanie/icons/linear';

import { defaultGuestsPickerConfig, GuestsPickerConfig } from './guests-picker.config';
import { GuestsPickerProps } from './guests-picker.props';
import { GuestsPickerItem, GuestsPickerItemProps, manageItemKey } from './guests-picker-item';

import { CommonModalsEnum, SessionStorageKeysEnum } from '../../../enums';
import { ensureElementVisible } from '../../../helpers/ensure-element-visible';
import { wordPluralisation } from '../../../helpers/words-pluralisation';
import { useFragmentActions, useModalSync } from '../../../hooks';
import { defaultGuestsPickerTranslation, GuestsPickerInputDataModel } from '../../../models';
import { Overlay } from '../../atoms';

const GuestsPicker = ({
    inputData,
    onInputDataChanged,
    config,
    flippable = false,
    noArrow = false,
    hideRooms = true,
    isMobileBrowser = true,
    triggerProps = {},
    saveToSession,
    children,
}: GuestsPickerProps): ReactNode => {
    const {
        roomsConfig,
        adultsConfig,
        totalGuestsCountConfig,
        childrenConfig,
        childAgeItemConfig,
        translationData,
    }: Required<GuestsPickerConfig> = { ...defaultGuestsPickerConfig, ...config };
    const { open, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const recipe = useSlotRecipe({
        key: 'guestsPicker',
    });
    const styles = recipe();

    // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/35572
    const mobileSubmitBtnRef = useRef(null);

    const locationId = useId();

    const { addFragment, removeFragment } = useFragmentActions(
        CommonModalsEnum.GuestPicker,
        locationId,
    );

    const getInputData = (inputData: GuestsPickerProps['inputData']) => {
        const data = inputData
            ? typeof inputData === 'string'
                ? JSON.parse(inputData)
                : inputData
            : {
                  roomsCount: roomsConfig.default,
                  adultsCount: adultsConfig.default,
                  childrenCount: childrenConfig.default,
              };

        if (hideRooms && 'roomsCount' in data) {
            delete data.roomsCount;
        }

        return data;
    };

    const initialState = getInputData(inputData);

    const [componentState, setComponentState] = useState<GuestsPickerInputDataModel>({
        ...initialState,
    });
    const [previousComponentState, setPreviousComponentState] =
        useState<GuestsPickerInputDataModel>({ ...initialState });
    const [isPickerPending, setIsPickerPending] = useState<boolean>(false);

    useEffect(() => {
        if (isEqual(initialState, previousComponentState)) {
            return;
        }

        setComponentState({ ...initialState });
        setPreviousComponentState({ ...initialState });

        onInputDataChanged && onInputDataChanged(initialState);
    }, [inputData]);

    useEffect(() => {
        if (open || isPopoverOpen) {
            return;
        }

        if (isEqual(previousComponentState, componentState)) {
            return;
        }

        setPreviousComponentState({ ...componentState });

        onInputDataChanged && onInputDataChanged(componentState);
    }, [open, isPopoverOpen]);

    const [getRootElementKeys] = useState<Record<manageItemKey, string>>({
        roomsCount: uuid(),
        adultsCount: uuid(),
        childrenAges: uuid(),
        childrenCount: uuid(),
    });

    const totalCount = useMemo(
        () => (componentState.adultsCount ?? 0) + (componentState.childrenCount ?? 0),
        [componentState],
    );

    const inputLabel = useMemo(() => {
        const { childrenCount = 0, adultsCount = 1 } = componentState;
        const count = childrenCount > 0 ? childrenCount : adultsCount;
        const { singular, plural, genitivePlural } =
            translationData.totalGuestLabel ?? defaultGuestsPickerTranslation.totalGuestLabel;
        const guestlabel = wordPluralisation(count, singular, plural, genitivePlural);
        const childrenLabel = childrenCount ? `+ ${childrenCount} ` : '';

        return `${adultsCount} ${childrenLabel}${guestlabel}`;
    }, [componentState, translationData.totalGuestLabel]);

    const totalGuestsMax = totalGuestsCountConfig.max ?? totalGuestsCountConfig.default ?? 99;
    const guestsMaxValues = {
        adults: adultsConfig.max
            ? Math.min(totalGuestsMax - (componentState.childrenCount ?? 0), adultsConfig.max)
            : undefined,
        children: childrenConfig.max
            ? Math.min(totalGuestsMax - (componentState.adultsCount ?? 0), childrenConfig.max)
            : undefined,
    };

    const incrementGuestsDisabled = totalCount >= totalGuestsMax;

    const isValid = useMemo(() => {
        const { childrenAges } = componentState;

        return (
            totalCount <= (totalGuestsCountConfig.max ?? 0) &&
            (childrenAges || []).every(age => !isNil(age))
        );
    }, [componentState, totalCount]);

    const handleOnGuestsInputDataChanged = (key: manageItemKey, value?: number) => {
        if (key === 'childrenAges') {
            return;
        }

        const oldValue = componentState[key];
        const newState = cloneDeep(componentState);
        newState[key] = value;

        if (key === 'childrenCount') {
            switch (true) {
                case (oldValue ?? 0) > (value ?? 0):
                    newState.childrenAges = (newState.childrenAges ?? []).slice(
                        0,
                        -((newState.childrenAges?.length ?? 0) - (value ?? 0)),
                    );
                    break;
                case (oldValue ?? 0) < (value ?? 0):
                    newState.childrenAges = newState.childrenAges ?? [];
                    while (newState.childrenAges.length !== value) {
                        newState.childrenAges.push(childAgeItemConfig.default);
                    }
                    break;
            }
        }

        setComponentState(newState);
    };

    const componentRootElements = useMemo<GuestsPickerItemProps[]>(() => {
        const fields: GuestsPickerItemProps[] = [
            {
                key: getRootElementKeys['adultsCount'],
                itemKey: 'adultsCount',
                name: 'adultsCount',
                config: { ...adultsConfig, max: guestsMaxValues.adults },
                label: translationData.adultsLabel,
                value: componentState.adultsCount,
                isFocusable: true,
                // prevent keyboard opening on mobile which causes dialog 'jumping'
                autoFocus: !isMobileBrowser,
                onValueChanged: handleOnGuestsInputDataChanged,
                disableIncrementValue: incrementGuestsDisabled,
                'data-testid': 'guests-picker-adults',
            },
            {
                key: getRootElementKeys['childrenCount'],
                itemKey: 'childrenCount',
                name: 'childrenCount',
                config: { ...childrenConfig, max: guestsMaxValues.children },
                label: translationData.childrenLabel,
                value: componentState.childrenCount,
                isFocusable: true,
                onValueChanged: handleOnGuestsInputDataChanged,
                disableIncrementValue: incrementGuestsDisabled,
                'data-testid': 'guests-picker-children',
            },
        ];

        if (!hideRooms) {
            fields.splice(0, 0, {
                key: getRootElementKeys['roomsCount'],
                itemKey: 'roomsCount',
                config: roomsConfig,
                name: 'roomsCount',
                label: translationData.roomsLabel,
                value: componentState.roomsCount,
                isFocusable: true,
                onValueChanged: handleOnGuestsInputDataChanged,
            });
        }

        return fields;
    }, [componentState]);

    const handleOnChildAgeChange = (index: number, newValue?: number) => {
        const newState = cloneDeep(componentState);

        if (!newState.childrenAges) {
            newState.childrenAges = [];
        }

        newState.childrenAges[index] = newValue;

        setComponentState(newState);
    };

    const renderGuestsPickerItem = (key: string, props: GuestsPickerItemProps): JSX.Element => (
        <GuestsPickerItem key={key} {...props} />
    );
    const renderChildrenSection = (childrenCount?: number, isMobileBrowser = true) => {
        if (!childrenCount) {
            return null;
        }

        const debouncedChildrenAges = componentState.childrenAges?.slice(0, childrenCount);

        return (
            <Box my={3}>
                <Text fontWeight={'bold'} mb={2}>
                    {translationData.childrenAgesLabel}
                </Text>
                {debouncedChildrenAges?.some(age => isNil(age)) ? (
                    <Text
                        mb={2}
                        fontSize="xs"
                        color="red.solid"
                        data-testid={'guests-picker-children-age-error'}
                    >
                        {translationData.childrenAgesRequiredMessage}
                    </Text>
                ) : undefined}
                <Flex
                    flexDirection={'column'}
                    gap={2}
                    overflowY={isMobileBrowser ? undefined : 'auto'}
                    maxH={isMobileBrowser ? undefined : '10rem'}
                    mr={-3}
                    pr={3}
                >
                    {debouncedChildrenAges?.map((age, index) =>
                        renderGuestsPickerItem(String(index), {
                            itemKey: 'childrenAges',
                            label: `${index + 1}`,
                            value: age,
                            config: childAgeItemConfig,
                            isFocusable: true,
                            isRequired: true,
                            name: `childrenAges-${index}`,
                            placeholder: translationData.childLabel,
                            'data-testid': 'guests-picker-children-age-select',
                            onValueChanged: (key, newValue) =>
                                handleOnChildAgeChange(index, newValue),
                        }),
                    )}
                </Flex>
            </Box>
        );
    };

    const onMobileDrawerClick = (): void => {
        if (open) {
            onSubmitChange();
            return;
        }

        setPreviousComponentState({ ...componentState });
        onDrawerOpen();
        setIsPickerPending(false);
        addFragment();
    };

    const closeDrawer = useCallback(() => {
        setTimeout(() => {
            onDrawerClose();
            removeFragment();
        }, 0);
    }, [onDrawerClose, removeFragment]);

    const onSubmitChange = useCallback((): void => {
        setIsPickerPending(false);

        if (!isValid) {
            return;
        }

        setTimeout(() => {
            isMobileBrowser ? closeDrawer() : setIsPopoverOpen(false);
        }, 0);
    }, [closeDrawer, isMobileBrowser, isValid]);

    const onCancelChange = useCallback((): void => {
        setComponentState({ ...previousComponentState });
        isMobileBrowser ? closeDrawer() : setIsPopoverOpen(false);
    }, [closeDrawer, isMobileBrowser, previousComponentState]);

    useModalSync({
        modalName: CommonModalsEnum.GuestPicker,
        onOpen: onDrawerOpen,
        onClose: () => {
            onCancelChange();
            onDrawerClose();
        },
        isOpen: open,
        fragmentId: locationId,
    });

    useEffect(() => {
        if (!isPopoverOpen) {
            return;
        }
        const onEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setTimeout(() => onCancelChange(), 0);
            }
        };

        document.addEventListener('keydown', onEsc);

        return () => {
            document.removeEventListener('keydown', onEsc);
        };
    }, [isPopoverOpen, onCancelChange]);

    const [debouncedChildrenCount, setDebouncedChildrenCount] = React.useState(
        componentState.childrenCount,
    );
    useDebounce(
        () => {
            setDebouncedChildrenCount(componentState.childrenCount);
        },
        200,
        [componentState.childrenCount],
    );

    const GuestPickerContent = ({
        isMobileBrowser = true,
        debouncedChildrenCount = componentState.childrenCount,
    }): JSX.Element => (
        <>
            {componentRootElements.map(({ key, ...rootElement }) =>
                renderGuestsPickerItem(String(key), { ...rootElement }),
            )}
            {renderChildrenSection(debouncedChildrenCount, isMobileBrowser)}
        </>
    );

    const onChangeSubmit = () => {
        setIsPickerPending(true);
        setTimeout(() => {
            onSubmitChange();
            if (
                !sessionStorage.getItem(SessionStorageKeysEnum.CriteriaChanged) &&
                (isMobileBrowser || saveToSession)
            ) {
                sessionStorage.setItem(SessionStorageKeysEnum.CriteriaChanged, 'true');
            }
        }, 0);
    };

    const arrow = useMemo(() => {
        if (noArrow) {
            return null;
        }

        if (open || isPopoverOpen) {
            return <IconsLinear.ArrowUp2 size={'2xs'} ml={'auto'} />;
        }

        return <IconsLinear.ArrowDown2 size={'2xs'} ml={'auto'} />;
    }, [isPopoverOpen, noArrow, open]);

    const renderTriggerElement = (loading?: boolean): ReactElement => {
        const functionalTriggerProps = {
            ref: isMobileBrowser ? mobileSubmitBtnRef : undefined,
            onClick: isMobileBrowser
                ? () => {
                      setIsPickerPending(true);
                      requestAnimationFrame(() => {
                          onMobileDrawerClick();
                      });
                  }
                : (ev: React.MouseEvent<HTMLElement>) => {
                      if (isPopoverOpen && !isValid) {
                          ev.preventDefault();
                      }
                  },
        };

        const commonTriggerProps = {
            colorPalette: 'gray',
            alignItems: 'center',
            justifyContent: 'start',
            fontWeight: 'normal',
            lineHeight: 1,
            borderColor: '{colors.border}',
            'data-testid': 'guests-picker',
            ...functionalTriggerProps,
            ...triggerProps,
        };

        const customTriggerElement = children
            ? React.cloneElement(children, {
                  ...functionalTriggerProps,
                  loading,
              })
            : undefined;

        if (isMobileBrowser) {
            return (
                customTriggerElement ?? (
                    <Button {...commonTriggerProps} minW={'128px'}>
                        <IconsLinear.User {...styles.icon} />
                        <Text
                            as={'span'}
                            flex={1}
                            textAlign={'left'}
                            data-testid={'guest-picker-label'}
                        >
                            {isPickerPending ? (
                                <Skeleton width={'80px'} height={'20px'} />
                            ) : (
                                inputLabel
                            )}
                        </Text>
                        {arrow}
                    </Button>
                )
            );
        }

        return (
            customTriggerElement ?? (
                <Button minW={'148px'} {...commonTriggerProps} _hover={{ bg: 'inherit' }}>
                    <IconsLinear.User mr={1} size={'md'} {...styles.icon} />
                    <Text as={'span'} lineHeight={1} mr={2}>
                        {inputLabel}
                    </Text>
                    {arrow}
                </Button>
            )
        );
    };

    const renderDesktopVersion = (): JSX.Element => {
        return (
            <Popover.Root
                lazyMount={true}
                size="md"
                open={isPopoverOpen}
                onOpenChange={details => {
                    setTimeout(() => setIsPopoverOpen(details.open), 0);
                }}
                positioning={{
                    placement: 'bottom',
                    overflowPadding: 0,
                    fitViewport: false,
                    flip: flippable,
                }}
                closeOnInteractOutside={isValid}
            >
                <Popover.Trigger asChild>{renderTriggerElement()}</Popover.Trigger>
                <Overlay isActive={isPopoverOpen} bgColor={'transparent'}>
                    <Popover.Positioner>
                        <Popover.Content
                            p={3}
                            maxHeight={'432px'}
                            ref={content => {
                                if (content && !flippable) {
                                    ensureElementVisible(content);
                                }
                            }}
                            data-testid={'guests-picker-popover'}
                        >
                            <Popover.Arrow>
                                <Popover.ArrowTip />
                            </Popover.Arrow>
                            {GuestPickerContent({ isMobileBrowser: false, debouncedChildrenCount })}
                            <HStack mt={3}>
                                <Button
                                    variant={'solid'}
                                    onClick={onChangeSubmit}
                                    loading={isPickerPending}
                                    disabled={!isValid}
                                    css={styles.button}
                                    data-testid={'guests-picker-submit'}
                                >
                                    {translationData.actionSubmit}
                                </Button>
                                <Button
                                    colorPalette={'secondary'}
                                    flexGrow={1}
                                    variant="outline"
                                    data-testid={'guests-picker-cancel'}
                                    onClick={() => setTimeout(() => onCancelChange(), 0)}
                                >
                                    {translationData.actionCancel}
                                </Button>
                            </HStack>
                        </Popover.Content>
                    </Popover.Positioner>
                </Overlay>
            </Popover.Root>
        );
    };

    const renderMobileVersion = (): JSX.Element => {
        return (
            <>
                {renderTriggerElement(isPickerPending)}

                <Drawer.Root
                    open={open}
                    placement="bottom"
                    onOpenChange={details => {
                        setTimeout(() => (!details.open ? onCancelChange() : undefined), 0);
                    }}
                    // finalFocusRef={mobileSubmitBtnRef}
                    variant={'overlap'}
                >
                    <Portal>
                        <Drawer.Backdrop />
                        <Drawer.Positioner>
                            <Drawer.Content
                                mb={0}
                                top={'auto'}
                                bottom={0}
                                data-testid={'guests-picker-drawer'}
                            >
                                <Drawer.CloseTrigger asChild>
                                    <CloseButton data-testid={'guests-picker-close'}>
                                        <IconsLinear.Close />
                                    </CloseButton>
                                </Drawer.CloseTrigger>
                                <Drawer.Header
                                    fontSize="md"
                                    borderBottomWidth={1}
                                    mb={3}
                                    fontWeight={'bold'}
                                >
                                    {translationData.mobileLabel}
                                </Drawer.Header>
                                <Drawer.Body py={0}>
                                    {GuestPickerContent({ isMobileBrowser: true })}
                                </Drawer.Body>
                                <Drawer.Footer gap={6} borderTopWidth={1} mt={3}>
                                    <Button
                                        css={styles.button}
                                        variant={'solid'}
                                        onClick={onChangeSubmit}
                                        loading={isPickerPending}
                                        disabled={!isValid}
                                        data-testid={'guests-picker-submit'}
                                    >
                                        {translationData.actionSubmit}
                                    </Button>
                                </Drawer.Footer>
                            </Drawer.Content>
                        </Drawer.Positioner>
                    </Portal>
                </Drawer.Root>
            </>
        );
    };

    return isMobileBrowser ? renderMobileVersion() : renderDesktopVersion();
};

export { GuestsPicker };
